import React from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { taskActualApis } from "../../../api/TaskSchedule";

// Bulk "Monthly Actuals" upload — rows are {Task ID, Month, Actual Qty,
// Remarks}: one row per task-month (LONG layout only; actuals arrive
// month-at-a-time). Thin wrapper over the shared staged-upload modal.
// UNLIKE the schedule upload, the commit replaces ONLY the months present in
// the file for each task — an August-only file leaves June/July history
// untouched. A month with qty 0 is stored as 0 (the way to erase a wrong
// month via Excel). Over-execution of the cumulative total, out-of-window and
// future-dated months are WARNINGS in the preview — never blocking.

const generateTaskActualTemplate = () => {
  const headers = ["Task ID", "Month", "Actual Qty", "Remarks"];
  const sampleRows = [
    ["AE-0100/B3.1.1", "Jul 2026", 1820.5, ""],
    ["AE-0100/B3.1.1(1)", "Jul 2026", 1650.25, "Face flooded 4 days"],
    ["AE-0100/B1.1", "Jul 2026", 28100.4, ""],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 44 }];

  const instructions = [
    ["Monthly Actuals Upload — Instructions"],
    [""],
    ["Task ID", "Required. The task's number (sequence id) shown in the app, within THIS project — including any (1)/(2) suffix. One row per task-month."],
    ["Month", "Required. e.g. 'Jul 2026', 'July 2026', '2026-07', or the short 'Jan26' form (2-digit year = 20xx)."],
    ["Actual Qty", "Required. The quantity actually executed in that month. 0 is allowed (erases a previously uploaded wrong month); negatives are rejected."],
    ["Remarks", "Optional site note — why the month ran short/over, e.g. 'face flooded 4 days'."],
    [""],
    ["Replace rule", "Uploading replaces ONLY the months present in the file for each task — other months of the same task keep their history. Re-uploading the same file is idempotent."],
    ["Only quantity tasks", "Only quantity-based tasks (with a work quantity) can capture actuals. Rows for Open tasks are rejected."],
    ["Duplicates", "The same Task ID + Month may appear only once in the file — duplicates are rejected."],
    ["Over-execution check", "If a task's CUMULATIVE actuals (existing months + this file) exceed its work quantity by more than 1 unit, its rows get a WARNING. Under-execution is normal and never flagged. Warnings never block the save."],
    ["Future months", "Months after the current month get a WARNING (usually a wrong year) — they still save."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 32 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Monthly Actuals");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "task-actual-upload-template.xlsx");
};

const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error)
    ? record.Error
    : record?.Error
    ? [record.Error]
    : [];

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      if (record?.status === "Applied") return <Tag color="blue">Saved</Tag>;
      if (record?.Warning) return <Tag color="gold">Warning</Tag>;
      return <Tag color="green">Valid</Tag>;
    },
  },
  { title: "Task ID", dataIndex: "TaskID", key: "TaskID", width: 160 },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 200, ellipsis: true },
  { title: "Month", dataIndex: "Month", key: "Month", width: 100 },
  { title: "Actual Qty", dataIndex: "ActualQty", key: "ActualQty", width: 120, align: "right" as const },
  {
    title: "Warning",
    dataIndex: "Warning",
    key: "Warning",
    width: 240,
    render: (value: any) =>
      value ? (
        <span style={{ color: "#b58a1f", fontSize: 12 }}>{value}</span>
      ) : null,
  },
  {
    title: "Remarks",
    dataIndex: "Remarks",
    key: "Remarks",
    width: 200,
    ellipsis: true,
    render: (value: any) =>
      value ? <span style={{ fontSize: 12 }}>{value}</span> : null,
  },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const warningRows = rows.filter((row) => !row?.Error && row?.Warning);
  const validRows = rows.filter((row) => !row?.Error);
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Valid (${validRows.length})`, rows: validRows },
    { key: "warnings", label: `Warnings (${warningRows.length})`, rows: warningRows },
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const summaryTags = (report: UploadReportState) => {
  const warningCount = report.rows.filter(
    (row: any) => !row?.Error && row?.Warning
  ).length;
  if (!warningCount) return null;
  return <Tag color="gold">{warningCount} warning(s)</Tag>;
};

const TaskActualUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Monthly Actuals (Excel)",
      entityLabel: "Monthly Actuals",
      templateSource: { kind: "client", generate: generateTaskActualTemplate },
      startUpload: (formData) => taskActualApis.uploadExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
      }),
      previewColumns,
      previewScrollX: 1380,
      bucketRows,
      summaryTags,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "task-actual-upload-report.xlsx",
        columns: [
          {
            header: "Status",
            width: 10,
            value: (row: any) =>
              row.Error
                ? "Error"
                : row.status === "Applied"
                ? "Saved"
                : row.Warning
                ? "Warning"
                : "Valid",
          },
          { header: "Task ID", width: 20, value: (row: any) => row.TaskID || "" },
          { header: "Task", width: 32, value: (row: any) => row.taskName || "" },
          { header: "Month", width: 10, value: (row: any) => row.Month || "" },
          { header: "Actual Qty", width: 14, value: (row: any) => row.ActualQty ?? "" },
          { header: "Warning", width: 44, value: (row: any) => row.Warning || "" },
          { header: "Remarks", width: 32, value: (row: any) => row.Remarks || "" },
          {
            header: "Error Details",
            width: 44,
            value: (row: any) =>
              Array.isArray(row.Error) ? row.Error.join("; ") : row.Error || "",
          },
        ],
      },
      rowErrorLabel: (row: any) => (row.TaskID ? `Task ${row.TaskID}` : "Row"),
      copy: {
        uploadIntro: (
          <p>
            Record the month's actual execution for many tasks at once — one row
            per task-month: <b>Task ID | Month | Actual Qty | Remarks</b>.
            Uploading replaces <b>only the file's months</b> for each task —
            earlier months keep their history. You'll see a preview before
            anything is saved.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Resolving each Task ID and month. This normally takes a few seconds to a few minutes depending on the file size.",
        committingHeading: "Saving actuals…",
        committingSub:
          "Writing each task's actual quantities for the file's months. Valid rows save; invalid rows are skipped.",
        commitCta: "Save Actuals",
        doneHeading: "Actuals saved",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} row(s) saved
            {report.failureCount ? `, ${report.failureCount} skipped` : ""}.
            The Monthly Execution report now reflects the file.
          </>
        ),
        creationFailedNote:
          "Some rows could not be saved — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId],
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default TaskActualUploadModal;
